import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Shield, Plus, Sparkles, ChevronDown, ChevronRight } from "lucide-react";

const sections = [
  {
    title: "Human Billing Expert Involvement",
    defaultOpen: true,
    count: 5,
    items: [
      "Human involvement in claim submission, EOB posting, payment reconciliation, patient billing, and insurance verification",
      "US-based dental billing expert with 8+ years of experience advising dental offices",
      "AI-certified specialist who strategically improves your collection rate",
      "Expert fills in where AI has gaps or needs review",
      "Full strategic consulting on your revenue cycle",
    ],
    note: "Denial management: AI handles first. Unresolved denials go to your office since you have more clinical context.",
  },
  {
    title: "Front Office AI Products",
    defaultOpen: false,
    count: 8,
    items: [
      "Amy (Receptionist)", "Leena (Schedule Optimizer)", "Stephanie (Hygiene Recall)",
      "Elise (Patient Experience)", "Kim (Case Acceptance)", "Morgan (Patient Intake)",
      "Trisha (Practice Manager)", "Joseph (Fax Management)",
    ],
  },
  {
    title: "Communication + Marketing Suite",
    defaultOpen: false,
    count: 12,
    items: [
      "VoIP Phone System", "Physical Desk Phones (Yealink)", "2-Way Texting",
      "Universal Inbox", "Team Chat & Channels", "Smart Voice Logs",
      "Automated Workflows", "Intelligent Fax", "Next-Gen Forms",
      "SMS & Email Campaigns", "Text-to-Pay Payments", "Payment Card Readers",
    ],
  },
  {
    title: "Back Office AI Products",
    defaultOpen: false,
    count: 8,
    items: [
      "Amanda (Verification)", "Ariel (Claim Submission)", "Alayna (Claim Scrubbing)",
      "Sarah (Denial Mgmt)", "Kianna (Patient Billing)", "Julia (AR & Collections)",
      "Alisha (EOB Posting)", "Laura (Insurance Calls)",
    ],
  },
  {
    title: "PMS + Imaging",
    defaultOpen: false,
    count: 12,
    items: [
      "Patient Charts", "Perio Charting", "Ledgers & Billing", "Treatment Planning",
      "Scheduling", "eClaims Built-In", "Cloud-Based — No Server", "Reporting & Analytics",
      "SOTA Cloud Imaging", "AI Copilot", "AI Scribe", "AI Treatment Planner",
    ],
  },
];

const addOns = [
  { name: "Human Denial Management", price: "+1%", desc: "Our billing team deeply involved in working denials" },
  { name: "SOTA Cloud Imaging", price: "$159/mo", desc: "First 3 months free" },
  { name: "PMS Migration Support", price: "$399/mo", desc: "Accelerated migration from your current PMS into Asha" },
];

const AccordionSection = ({ section, isFirst }) => {
  const [open, setOpen] = useState(section.defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          open ? "bg-blue-50/60" : "hover:bg-slate-50"
        } ${isFirst ? "rounded-t-xl" : ""}`}
      >
        <div className="flex items-center gap-2.5">
          {open ? (
            <ChevronDown className="w-4 h-4 text-blue-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          )}
          <span className={`text-sm font-semibold ${open ? "text-blue-700" : "text-slate-700"}`}>
            {section.title}
          </span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          open ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400"
        }`}>
          {section.count}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 bg-blue-50/30">
          <div className={`${section.items.length > 5 ? "grid grid-cols-2 gap-x-4 gap-y-1.5" : "space-y-1.5"}`}>
            {section.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-[13px] text-slate-600">{item}</span>
              </div>
            ))}
          </div>
          {section.note && (
            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-blue-100">
              <Shield className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-[13px] text-slate-600">{section.note}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ManagedServiceSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="w-full flex flex-col h-full relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-5 flex-shrink-0"
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-2 border border-blue-100">
            <Sparkles className="w-3 h-3" />
            Option 1
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Managed Service
          </h2>
          <p className="text-base font-medium text-slate-700 leading-relaxed mt-2 max-w-3xl">
            Everything in the Asha AI OS, plus a dedicated US-based dental billing expert
            who works alongside your AI team to maximize collections.
          </p>
        </motion.div>

        {/* Two separate columns */}
        <div className="flex gap-5 flex-1 min-h-0">
          {/* Left: Pricing + What's included in one card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex-1 bg-white rounded-2xl border border-blue-200 shadow-sm flex flex-col overflow-hidden min-h-0"
          >
            {/* Pricing header inside card */}
            <div className="px-5 pt-4 pb-3 border-b border-blue-100 flex items-center justify-between flex-shrink-0">
              <p className="text-xs font-bold text-blue-500 uppercase tracking-wider">
                Pricing & what's included
              </p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-extrabold text-slate-900">$799</span>
                <span className="text-xs text-slate-500">/mo</span>
                <span className="text-lg text-slate-300 mx-0.5">+</span>
                <span className="text-2xl font-extrabold text-slate-900">3%</span>
                <span className="text-xs text-slate-400 ml-1">of total collections</span>
              </div>
            </div>

            {/* Accordion sections */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {sections.map((section, i) => (
                <AccordionSection key={i} section={section} isFirst={i === 0} />
              ))}
            </div>
          </motion.div>

          {/* Right: Add-ons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-[380px] bg-[#0f172a] rounded-2xl shadow-xl p-5 flex flex-col text-white"
          >
            <div className="flex items-center gap-2 mb-4">
              <Plus className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Optional add-ons
              </span>
            </div>

            <div className="space-y-3 flex-1">
              {addOns.map((addon, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-xl ring-1 ring-white/10 px-5 py-4"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-base font-bold text-white">
                      {addon.name}
                    </span>
                    <span className="text-lg font-extrabold text-blue-400">
                      {addon.price}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">{addon.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-sm text-slate-400 leading-relaxed">
                Best used by migrating to our AI-Native PMS, with white-glove
                migration support to get you live cleanly and quickly.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ManagedServiceSlide;
